
import Link from '@/components/compat/Link';
import Image from '@/components/compat/Image';
import type { ReactNode } from 'react';
import {
  ChevronRight,
  Calendar,
  Store,
  Tag,
  Infinity,
  Lock,
  Crown,
} from 'lucide-react';
import {
  HeroBackdropLayers,
  ImageBackdropLayers,
  GsImage,
  XatsPriceValue,
  cn,
  heroDescriptionDetailClass,
} from '@/components/help-center/latest-power-shared';
import { PawnBadgeIcon, SmileySvgBadgeIcon } from '@/components/help-center/powers/power-gallery-view';
import {
  type PowerInfo,
  getPowerCategoryLabel,
  formatStorePrice,
  formatFairtradePrice,
  formatReleaseDate,
} from '@/lib/powers-data';
import { getPowerCategoryAccent, getPowerCategoryBadgeStyle } from '@/lib/power-category-theme';
import {
  POWER_BUY_NOW_URL,
  POWER_TRADE_URL,
  POWER_TRADE_ICON_SRC,
  POWER_FAIRTRADE_ICON_SRC,
  POWER_BUY_NOW_BUTTON_STYLE,
  POWER_TRADE_BUTTON_STYLE,
  powerBuyNowButtonClass,
  powerCtaSeparatorClass,
  powerTradeButtonClass,
} from '@/components/help-center/powers/power-shared';
import PowerFairtradeHistoryDialog from '@/components/help-center/powers/PowerFairtradeHistoryDialog';
import { PowerDescriptionContent } from '@/components/help-center/powers/PowerMentionText';
import { PortalCrystalSeparator } from '@/components/help-center/PortalCrystalSeparator';
import { formatPowerDisplayName } from '@/lib/power-mention-data';
import { analytics } from '@/services/analytics';

// Replica exata do powerInfoRowClass da homepage
const infoRowClass = cn(
  'flex w-full min-h-[42px] items-center justify-between gap-3 rounded-2xl px-3.5 py-2.5',
  'border border-white/[0.08] bg-gradient-to-br from-white/[0.07] to-white/[0.02]',
  'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm',
  'transition-colors duration-200 hover:border-sky-400/25 hover:from-white/[0.09]'
);

function InfoRow({
  icon: Icon,
  iconSrc,
  label,
  value,
}: {
  icon?: React.ElementType;
  iconSrc?: string;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className={infoRowClass}>
      <span className="flex min-w-0 items-center gap-2 font-poppins text-[10px] font-semibold uppercase tracking-wide text-sky-200/75">
        {iconSrc ? (
          <Image
            src={iconSrc}
            alt=""
            width={14}
            height={14}
            className="h-3.5 w-3.5 shrink-0 object-contain"
            aria-hidden
          />
        ) : Icon ? (
          <Icon className="h-3.5 w-3.5 shrink-0 text-sky-300/90" aria-hidden />
        ) : null}
        {label}
      </span>
      <div className="min-w-0 text-right">{value}</div>
    </div>
  );
}

function PriceValue({ text, yellow = false }: { text: string; yellow?: boolean }) {
  if (text === 'Gratuito' || text === '—' || /days?$/i.test(text)) {
    return (
      <span
        className={cn(
          'font-poppins text-xs font-semibold sm:text-sm',
          yellow ? 'text-amber-300' : 'text-blue-50'
        )}
      >
        {text}
      </span>
    );
  }
  return (
    <XatsPriceValue className={yellow ? 'text-amber-300' : 'text-blue-50'}>
      {text}
    </XatsPriceValue>
  );
}

export default function PowerDetailHero({
  power,
  compact = false,
  disableExternalLinks = false,
  editAction,
}: {
  power: PowerInfo;
  /** Padding reduzido para pré-visualização no painel. */
  compact?: boolean;
  /** Desactiva links Trade/Comprar (modo editor). */
  disableExternalLinks?: boolean;
  /** Slot na linha dos breadcrumbs (ex.: botão Editar). */
  editAction?: ReactNode;
}) {
  const categoryAccent = getPowerCategoryAccent(power.category);
  const categoryBadgeStyle = getPowerCategoryBadgeStyle(power.category);
  const isSpecialFeature = power.slug === 'everypower' || power.slug === 'allpowers';
  const displayName = formatPowerDisplayName(power.slug);

  return (
    <section
      className={cn(
        'relative overflow-x-hidden bg-[#030023]',
        compact ? 'z-0 rounded-2xl' : 'z-30'
      )}
    >
      <div className="absolute inset-0 overflow-hidden" aria-hidden>
        <HeroBackdropLayers className="absolute inset-0" />
      </div>

      <div
        className={cn(
          'relative z-[20] mx-auto max-w-7xl px-6',
          compact ? 'pb-10 pt-10 md:pb-12 md:pt-12' : 'pb-16 pt-32 md:pb-24 md:pt-40'
        )}
      >
        {/* Breadcrumb + editar */}
        <div className="mb-10 flex items-center justify-between gap-3">
          <nav className="flex min-w-0 flex-wrap items-center gap-1.5 font-poppins text-sm">
            {disableExternalLinks ? (
              <>
                <span className="text-white/40">Início</span>
                <ChevronRight className="h-3.5 w-3.5 text-white/25" />
                <span className="text-white/40">Powers</span>
                <ChevronRight className="h-3.5 w-3.5 text-white/25" />
                <span className="text-white/80">{displayName}</span>
              </>
            ) : (
              <>
                <Link href="/" className="text-white/40 transition-colors hover:text-white/70">
                  Início
                </Link>
                <ChevronRight className="h-3.5 w-3.5 text-white/25" />
                <Link href="/powers" className="text-white/40 transition-colors hover:text-white/70">
                  Powers
                </Link>
                <ChevronRight className="h-3.5 w-3.5 text-white/25" />
                <span className="text-white/80">{displayName}</span>
              </>
            )}
          </nav>
          {editAction ? <div className="shrink-0">{editAction}</div> : null}
        </div>

        <div className="grid lg:grid-cols-[1fr_400px] gap-12 lg:gap-20 items-start">

          {/* ── Left column ────────────────────────────────── */}
          <div className="flex flex-col gap-8">
            {/* Icon + title */}
            <div className="flex items-start gap-6">
              {/* Power icon — orbital radial style (igual ao card da homepage) */}
              <div
                className="flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl overflow-hidden"
                style={{
                  background:
                    'radial-gradient(circle at 50% 48%, #112268 0%, #0a1450 42%, #060c38 75%, #030719 100%)',
                  boxShadow:
                    '0 0 0 1.5px #0b1658, 0 0 0 2.5px #0f1e70, 8px 12px 28px rgba(0,0,0,0.55)',
                }}
              >
                <GsImage
                  code={power.slug}
                  className="flex h-full w-full items-center justify-center"
                  imgClassName="max-h-[72px] max-w-[72px] object-contain"
                />
              </div>

              {/* Name + badges */}
              <div className="flex flex-col gap-3 min-w-0 pt-1">
                <div className="flex items-center flex-wrap gap-2">
                  <span
                    className="inline-flex items-center rounded-full border px-3 py-1 font-poppins text-xs font-bold"
                    style={categoryBadgeStyle}
                  >
                    {getPowerCategoryLabel(power.category)}
                  </span>
                  {power.id > 0 && (
                    <span
                      className="inline-flex items-center rounded-full px-2.5 py-0.5 font-poppins text-[11px] font-bold tabular-nums"
                      style={{
                        background: 'linear-gradient(135deg, #0e1f7a 0%, #091460 100%)',
                        color: '#8aabf8',
                        boxShadow: '0 0 0 1.5px #1a3099',
                      }}
                    >
                      ID {power.id}
                    </span>
                  )}
                </div>
                <h1
                  className="text-4xl md:text-5xl font-bold text-white font-poppins tracking-tight leading-tight"
                  style={{ textShadow: '0 0 28px rgba(255,255,255,0.18), 0 0 8px rgba(255,255,255,0.08)' }}
                >
                  {displayName}
                </h1>
              </div>
            </div>

            {/* Description */}
            <p className={cn('max-w-2xl', heroDescriptionDetailClass)}>
              <PowerDescriptionContent
                text={power.description.short || power.description.long}
                className="[&_a]:text-sky-200 [&_a]:underline"
                as="span"
              />
            </p>

            {/* Meta chips */}
            <div className="flex items-center gap-2.5 flex-wrap">
              {isSpecialFeature ? (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 font-poppins text-xs font-bold uppercase tracking-widest text-white"
                  style={{
                    background: 'linear-gradient(90deg, #059669 0%, #10b981 50%, #34d399 100%)',
                    boxShadow: '0 0 0 1.5px rgba(16,185,129,0.40), 0 4px 14px rgba(5,150,105,0.35)',
                  }}
                >
                  <Crown className="h-3 w-3" />
                  Funcionalidade especial
                </span>
              ) : power.limited ? (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 font-poppins text-xs font-bold uppercase tracking-widest text-white"
                  style={{
                    background: 'linear-gradient(90deg, #e8620a 0%, #f47c1a 50%, #e8a020 100%)',
                    boxShadow: '0 0 0 1.5px rgba(240,130,30,0.40), 0 4px 14px rgba(220,90,10,0.35)',
                  }}
                >
                  <Lock className="h-3 w-3" />
                  Limitado
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-cyan-500/30 to-sky-500/30 px-4 py-1.5 font-poppins text-xs font-bold uppercase tracking-widest text-cyan-300 ring-1 ring-cyan-400/35">
                  <Infinity className="h-3 w-3" />
                  Ilimitado
                </span>
              )}
              {power.smilies.length > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.1] bg-white/[0.06] px-3 py-1.5 font-poppins text-xs font-semibold text-blue-100/70 backdrop-blur-sm">
                  <SmileySvgBadgeIcon className="text-blue-100/70" />
                  {power.smilies.length} smilies
                </span>
              )}

              {/* Pawns count badge */}
              {power.pawns.length > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full border border-white/[0.1] bg-white/[0.06] px-3 py-1.5 font-poppins text-xs font-semibold text-blue-100/70 backdrop-blur-sm">
                  <PawnBadgeIcon className="text-blue-100/70" />
                  <span className="leading-none">
                    {power.pawns.length} peões
                  </span>
                </span>
              )}

              {!isSpecialFeature ? (
                <>
                  <span className={powerCtaSeparatorClass} aria-hidden />
                  {power.limited ? (
                    disableExternalLinks ? (
                      <span className={powerTradeButtonClass} style={POWER_TRADE_BUTTON_STYLE}>
                        <Image
                          src={POWER_TRADE_ICON_SRC}
                          alt=""
                          width={22}
                          height={22}
                          className="h-[22px] w-[22px] shrink-0 object-contain"
                          aria-hidden
                        />
                        Comprar no Trade
                      </span>
                    ) : (
                      <a
                        href={POWER_TRADE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={powerTradeButtonClass}
                        style={POWER_TRADE_BUTTON_STYLE}
                        onClick={() => {
                          void analytics.powerOpen({
                            powerId: power.id,
                            slug: power.slug,
                            action: 'open_power',
                          });
                        }}
                      >
                        <Image
                          src={POWER_TRADE_ICON_SRC}
                          alt=""
                          width={22}
                          height={22}
                          className="h-[22px] w-[22px] shrink-0 object-contain"
                          aria-hidden
                        />
                        Comprar no Trade
                      </a>
                    )
                  ) : disableExternalLinks ? (
                    <span className={powerBuyNowButtonClass} style={POWER_BUY_NOW_BUTTON_STYLE}>
                      <Store className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                      Comprar agora
                    </span>
                  ) : (
                    <a
                      href={POWER_BUY_NOW_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={powerBuyNowButtonClass}
                      style={POWER_BUY_NOW_BUTTON_STYLE}
                      onClick={() => {
                        void analytics.powerOpen({
                          powerId: power.id,
                          slug: power.slug,
                          action: 'open_power',
                        });
                      }}
                    >
                      <Store className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                      Comprar agora
                    </a>
                  )}
                </>
              ) : null}
            </div>
          </div>

          {/* ── Right column: card igual ao da homepage ──── */}
          <div className="relative mx-auto w-full max-w-[420px] lg:mx-0 lg:ml-auto">
            <div
              className={cn(
                'relative overflow-hidden rounded-2xl border border-blue-900/60 bg-blue-950/30',
                'shadow-xl shadow-black/35 ring-1 ring-sky-500/25',
                'transition-all duration-300 hover:border-sky-500/35 hover:shadow-blue-950/50'
              )}
            >
              {/* Camadas de fundo — mesmas da homepage */}
              <ImageBackdropLayers className="absolute inset-0 overflow-hidden rounded-2xl" />
              <div className="pointer-events-none absolute inset-0 bg-slate-950/72" aria-hidden />
              <div
                className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#030023]/45 via-transparent to-[#030023]/80"
                aria-hidden
              />

              <div className="relative z-10 flex flex-col gap-4 px-5 pb-5 pt-5">
                {/* Header "Informações" */}
                <div className="text-center">
                  <h3 className="font-poppins text-sm font-bold uppercase tracking-[0.2em] text-white md:text-base">
                    Informações
                  </h3>
                  <PortalCrystalSeparator className="mt-3.5" accentRgb="56, 189, 248" />
                </div>

                {/* Info rows — mesmo estilo da homepage */}
                <div className="space-y-2">
                  <InfoRow
                    icon={Calendar}
                    label="Data de lançamento:"
                    value={
                      <span className="font-poppins text-xs font-semibold text-white sm:text-sm">
                        {formatReleaseDate(power.released)}
                      </span>
                    }
                  />
                  <InfoRow
                    icon={Tag}
                    label="Categoria:"
                    value={
                      <span
                        className="font-poppins text-xs font-semibold sm:text-sm"
                        style={{ color: categoryAccent.hex }}
                      >
                        {getPowerCategoryLabel(power.category)}
                      </span>
                    }
                  />
                  <InfoRow
                    icon={Store}
                    label="Preço (loja):"
                    value={<PriceValue text={formatStorePrice(power.prices.store)} />}
                  />
                  <InfoRow
                    iconSrc={POWER_FAIRTRADE_ICON_SRC}
                    label="Fairtrade:"
                    value={
                      <div className="flex min-w-0 items-center justify-end gap-2">
                        <XatsPriceValue className="text-white">
                          {formatFairtradePrice(
                            power.prices.fairtrade_min,
                            power.prices.fairtrade_max
                          )}
                        </XatsPriceValue>
                        <PowerFairtradeHistoryDialog power={power} />
                      </div>
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
